import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { IPageFilters } from 'src/app/models/filters/interfaces/pageFilters.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';

export interface IFilterChipDisplay {
  type: 'individual' | 'aggregated';
  categoryName?: string;
  option?: ISelectOption;
  count?: number;
  options?: ISelectOption[];
}

@Component({
  selector: 'app-view-filters-multi-select',
  templateUrl: './view-filters-multi-select.component.html',
  styleUrls: ['./view-filters-multi-select.component.scss'],
})
export class ViewFiltersMultiSelectComponent implements OnInit, AfterViewChecked, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public pageFilters: IPageFilters<any, any>;

  public selectedOptions: ISelectOption[] = [];
  public filterChipsToDisplay: IFilterChipDisplay[] = [];
  private _previousHeight = 0;

  constructor(
    private readonly _el: ElementRef,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService
  ) {}

  ngOnInit(): void {
    // Get initial selected options
    this.selectedOptions = this.pageFilters.getAllSelectedOptions();
    this._processFilterChips();

    // Subscribe to updates
    this.pageFilters.onUpdate$.subscribe(() => {
      this.selectedOptions = this.pageFilters.getAllSelectedOptions();
      this._processFilterChips();
    });
  }

  ngAfterViewChecked() {
    this._handleAppliedFiltersBlockHeight();
  }

  ngOnDestroy() {
    this._activeFiltersHeightService.updateHeight(0);
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public fromDateSelected(value?: Date): void {
    this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.FromDate, value);
  }

  public toDateSelected(value?: Date): void {
    this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.ToDate, value);
  }

  public searchSubmitted(value?: string): void {
    this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.Search, value);
  }

  public clearSelectedFilters() {
    this.pageFilters.updateFiltersByKeyValue({}, true);
  }

  public clearSingleOption(optionToRemove: ISelectOption) {
    this.pageFilters.removeSelectedOptionsIfExist([optionToRemove]);
  }

  public clearAggregatedCategory(categoryName: string) {
    const optionsToRemove = this.selectedOptions.filter((opt) => opt.categoryName === categoryName);
    this.pageFilters.removeSelectedOptionsIfExist(optionsToRemove);
  }

  private _processFilterChips(): void {
    const groupedByCategory = new Map<string, ISelectOption[]>();

    // Group options by category
    this.selectedOptions.forEach((option) => {
      const category = option.categoryName || 'Other';
      if (!groupedByCategory.has(category)) {
        groupedByCategory.set(category, []);
      }
      groupedByCategory.get(category)!.push(option);
    });

    // Convert to display format
    this.filterChipsToDisplay = [];
    groupedByCategory.forEach((options, categoryName) => {
      if (options.length > 5) {
        // Show aggregated chip for categories with more than 5 values
        this.filterChipsToDisplay.push({
          type: 'aggregated',
          categoryName,
          count: options.length,
          options,
        });
      } else {
        // Show individual chips for categories with 5 or fewer values
        options.forEach((option) => {
          this.filterChipsToDisplay.push({
            type: 'individual',
            option,
          });
        });
      }
    });
  }

  private _handleAppliedFiltersBlockHeight() {
    const newHeight = this._el.nativeElement.offsetHeight;

    if (newHeight !== this._previousHeight) {
      this._previousHeight = newHeight;
      this._activeFiltersHeightService.updateHeight(newHeight);
    }
  }
}
